
import Vue from 'vue'
import Component from 'vue-class-component'
import { ArtistTrackModel } from '@/models'

interface MediaMetadataPayload {
    title: string;
    artist: string;
    album: string;
    artwork: Array<{
        src: string;
        sizes: string;
        type: string;
    }>;
}

interface MediaMetadataConstructor {
    new (metadata: MediaMetadataPayload): unknown;
}

interface MediaSessionNavigator extends Navigator {
    mediaSession: {
        metadata: unknown;
        setActionHandler: (action: string, handler: () => unknown) => void;
    };
}

interface MediaMetadataWindow extends Window {
    MediaMetadata: MediaMetadataConstructor;
}

@Component
export default class Player extends Vue {
    playing = false
    mutes = false
    currentTime = 0
    duration = 0
    volumeChangeHandler: (() => void) | null = null
    endedHandler: (() => Promise<void>) | null = null
    errorHandler: ((e: Event) => Promise<void>) | null = null
    timeUpdateHandler: (() => void) | null = null
    loadedMetadataHandler: (() => void) | null = null
    keydownHandler: ((e: KeyboardEvent) => void) | null = null

    get audio () {
        return this.$store.getters['getAudio']
    }
    get current (): ArtistTrackModel | null {
        return this.$store.getters['getCurrent']
    }
    get isShuffle () {
        return this.$store.getters['getShuffle']
    }
    get isRepeat () {
        return this.$store.getters['getRepeat']
    }
    get isRadio () {
        return this.$store.getters['getRadio']
    }
    get radioSource () {
        return this.$store.getters['getRadioSource']
    }
    set radioSource (val: string) {
        this.$store.commit('setRadioSource', val)
        if (this.isRadio) {
            this.$store.dispatch('startRadioPlayback').then((track) => {
                if (track) {
                    this.safePlay()
                }
            })
        }
    }
    get radioGenre () {
        return this.$store.getters['getRadioGenre']
    }
    set radioGenre (val: string) {
        this.$store.commit('setRadioGenre', val)
        if (this.isRadio) {
            this.$store.dispatch('startRadioPlayback').then((track) => {
                if (track) {
                    this.safePlay()
                }
            })
        }
    }
    get formattedCurrentTime() {
        return this.convertTime(Math.round(this.currentTime))
    }
    get formattedDuration() {
        return this.convertTime(Math.round(this.duration))
    }

    seekTo(event: Event) {
        const input = event.target as HTMLInputElement
        this.audio.currentTime = parseFloat(input.value)
    }

    convertTime (secs: number | string) {
        const totalSec = Math.floor(Number(secs) || 0);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        const minute = (min < 10) ? "0" + min : min;
        const seconds = (sec < 10) ? "0" + sec : sec;
        return (minute + ':' + seconds);
    }

    getCoverStyle(image?: string, name?: string) {
        if (image && image.trim() && image.indexOf('url("")') === -1) {
            return { backgroundImage: `url(${image})` }
        }
        const str = name || 'fallback';
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        const hue = Math.abs(hash) % 360;
        return {
            backgroundImage: `linear-gradient(135deg, hsl(${hue}, 70%, 20%), hsl(${(hue + 40) % 360}, 80%, 15%))`
        }
    }

    shuffle() {
        this.$store.commit('setShuffle')
    }
    repeat() {
        this.$store.commit('setRepeat')
    }
    blacklistCurrentArtist() {
        if (this.current) {
            this.$store.commit('blacklistArtist', this.current.artist_name)
            if (this.playing || this.isRadio) {
                this.playNext()
            }
        }
    }
    async toggleRadio() {
        this.$store.commit('setRadio')
        if (!this.isRadio) {
            return
        }
        if (this.current) {
            return
        }
        const track = await this.$store.dispatch('startRadioPlayback')
        if (track) {
            this.safePlay()
        }
    }
    async play () {
        if (!this.current) {
            // No track selected — start radio
            if (!this.isRadio) {
                this.$store.commit('setRadio')
            }
            const track = await this.$store.dispatch('startRadioPlayback')
            if (track) {
                this.safePlay()
            }
            return
        }
        this.safePlay()
    }
    async safePlay(skipAttempts = 4) {
        try {
            await this.audio.play()
            this.playing = true
            return true
        } catch (error) {
            const errorName = error instanceof DOMException ? error.name : ''

            if (errorName === 'NotAllowedError' || skipAttempts <= 0) {
                this.playing = false
                return false
            }

            if (this.current) {
                this.$store.commit('markTrackUnavailable', this.current)
            }

            const track = await this.$store.dispatch('playNextTrack')
            if (!track) {
                this.playing = false
                return false
            }

            return this.safePlay(skipAttempts - 1)
        }
    }
    pause () {
        this.audio.pause()
        this.playing = false
    }
    playNext () {
        this.$store.dispatch('playNextTrack').then((track) => {
            if (!track) {
                return
            }
            this.safePlay()
        })
    }
    async playPrevious () {
        const track = await this.$store.dispatch('playPreviousTrack')
        if (track) {
            await this.safePlay()
        }
    }
    times = '00:00'
    mute () {
        this.audio.muted = !this.audio.muted
        this.mutes = !this.mutes
    }
    
    saveVolume() {
        this.audio.muted = false
        localStorage.setItem('playerVolume', this.audio.volume.toString())
        this.mutes = false
    }

    mounted() {
        this.mutes = this.audio.muted
        this.timeUpdateHandler = () => {
            this.currentTime = this.audio.currentTime
            if (!isNaN(this.audio.duration)) {
                this.duration = this.audio.duration
            }
        }
        this.loadedMetadataHandler = () => {
            if (!isNaN(this.audio.duration)) {
                this.duration = this.audio.duration
            }
        }
        this.keydownHandler = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            if (e.code === 'Space') {
                e.preventDefault();
                if (this.playing) {
                    this.pause();
                } else {
                    this.play();
                }
            } else if (e.code === 'ArrowRight') {
                this.playNext();
            } else if (e.code === 'ArrowLeft') {
                this.playPrevious();
            }
        }
        this.volumeChangeHandler = () => {
            localStorage.setItem('playerVolume', this.audio.volume.toString())
            this.mutes = this.audio.muted
        }
        this.endedHandler = async () => {
            const track = await this.$store.dispatch('playNextTrack')
            if (track) {
                this.safePlay()
            }
        }
        this.errorHandler = async (e: Event) => {
            if (this.current) {
                this.$store.commit('markTrackUnavailable', this.current)
            }
            console.warn('Audio stream unavailable. Skipping to next track...', e)
            const track = await this.$store.dispatch('playNextTrack')
            if (track) {
                this.safePlay()
            }
        }
        this.audio.addEventListener('volumechange', this.volumeChangeHandler)
        this.audio.addEventListener('ended', this.endedHandler)
        this.audio.addEventListener('error', this.errorHandler as EventListener)
        this.audio.addEventListener('timeupdate', this.timeUpdateHandler)
        this.audio.addEventListener('loadedmetadata', this.loadedMetadataHandler)
        window.addEventListener('keydown', this.keydownHandler)

        if ('mediaSession' in navigator && 'MediaMetadata' in window) {
            const mediaSession = (navigator as MediaSessionNavigator).mediaSession
            const MediaMetadata = (window as MediaMetadataWindow).MediaMetadata
            mediaSession.setActionHandler('play', () => { this.play(); })
            mediaSession.setActionHandler('pause', () => { this.pause(); })
            mediaSession.setActionHandler('previoustrack', () => { this.playPrevious(); })
            mediaSession.setActionHandler('nexttrack', () => { this.playNext(); })

            this.$watch('current', (track: ArtistTrackModel | null) => {
                if (track) {
                    mediaSession.metadata = new MediaMetadata({
                        title: track.name,
                        artist: track.artist_name,
                        album: 'Robopol Music',
                        artwork: [
                            { src: track.image, sizes: '300x300', type: 'image/jpeg' },
                            { src: track.image, sizes: '500x500', type: 'image/jpeg' },
                            { src: track.image, sizes: '600x600', type: 'image/jpeg' }
                        ]
                    })
                }
            }, { immediate: true })
        }
    }

    beforeDestroy() {
        if (this.volumeChangeHandler) {
            this.audio.removeEventListener('volumechange', this.volumeChangeHandler)
        }
        if (this.endedHandler) {
            this.audio.removeEventListener('ended', this.endedHandler)
        }
        if (this.errorHandler) {
            this.audio.removeEventListener('error', this.errorHandler as EventListener)
        }
        if (this.timeUpdateHandler) {
            this.audio.removeEventListener('timeupdate', this.timeUpdateHandler)
        }
        if (this.loadedMetadataHandler) {
            this.audio.removeEventListener('loadedmetadata', this.loadedMetadataHandler)
        }
        if (this.keydownHandler) {
            window.removeEventListener('keydown', this.keydownHandler)
        }
    }

}
